/**
 * Helpers de presentación del dominio Equipo (traducción de error + categorías).
 *
 * Puro formato/traducción para la UI; sin lógica de negocio ni acceso a la API.
 */
import { ApiError } from "@/lib/api";

import {
  OTHER_CATEGORY_LABEL,
  PERMISSION_CATEGORIES,
  TEAM_ERROR_MESSAGE,
} from "./constants";
import type { PermissionItem } from "./types";

/**
 * Traduce un error de la API/red a un mensaje legible. Usa el diccionario por
 * `error_code` (`TEAM_ERROR_MESSAGE`) y, si el código no está mapeado, cae al
 * `message` del `ApiError` (ya en español del backend).
 */
export function teamErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return TEAM_ERROR_MESSAGE[error.error_code] ?? error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Ocurrió un error inesperado.";
}

/** Una categoría ya resuelta con sus permisos del catálogo (id + descripción). */
export interface ResolvedPermissionCategory {
  id: string;
  label: string;
  permissions: PermissionItem[];
}

/**
 * Agrupa el catálogo plano de permisos (`GET /permissions`) en las categorías del
 * SSR (`PERMISSION_CATEGORIES`), preservando su orden. Cada permiso conserva su
 * descripción. Los permisos del catálogo que no caen en ninguna categoría del SSR
 * se agrupan en "Otros" (para no perderlos en la UI). Las categorías que quedan
 * vacías (ningún permiso del catálogo) se omiten.
 */
export function groupPermissions(
  items: PermissionItem[],
): ResolvedPermissionCategory[] {
  const byId = new Map(items.map((item) => [item.id, item]));
  const seen = new Set<string>();
  const resolved: ResolvedPermissionCategory[] = [];

  for (const category of PERMISSION_CATEGORIES) {
    const permissions: PermissionItem[] = [];
    for (const id of category.permissions) {
      const item = byId.get(id);
      if (item) {
        permissions.push(item);
        seen.add(id);
      }
    }
    if (permissions.length > 0) {
      resolved.push({ id: category.id, label: category.label, permissions });
    }
  }

  // Permisos del catálogo no clasificados por el SSR: van a "Otros" en su orden
  // de aparición (no se descartan: la UI debe poder marcarlos).
  const others = items.filter((item) => !seen.has(item.id));
  if (others.length > 0) {
    resolved.push({ id: "other", label: OTHER_CATEGORY_LABEL, permissions: others });
  }

  return resolved;
}

/** Convierte un id de permiso a una etiqueta legible (`edit_orders` -> "Edit Orders"). */
export function permissionLabel(id: string): string {
  return id
    .split("_")
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(" ");
}
